import { Link, useLocation } from 'react-router-dom';
import { useAuth } from '../AuthContext';
import { 
  Menu, X, Home, CheckSquare, 
  Utensils, Calendar, Baby, LogOut, LogIn 
} from 'lucide-react';

const NavBar = ({ collapsed, setCollapsed }) => {
  const { user, login, logout } = useAuth();
  const location = useLocation();

  const navItems = [
    { path: '/', label: 'Home', icon: Home, color: 'hover:bg-gray-100' },
    { path: '/tasks', label: 'Tasks', icon: CheckSquare, color: 'hover:bg-blue-50 hover:text-blue-600' },
    { path: '/meals', label: 'Meals', icon: Utensils, color: 'hover:bg-orange-50 hover:text-orange-600' },
    { path: '/calendar', label: 'Calendar', icon: Calendar, color: 'hover:bg-indigo-50 hover:text-indigo-600' },
    { path: '/kids', label: 'Kids', icon: Baby, color: 'hover:bg-green-50 hover:text-green-600' },
  ];

  return (
    <>
      {/* 1. MOBILE FLOATING HAMBURGER (Visible only on mobile when menu is closed) */}
      <div className="md:hidden fixed top-4 left-4 z-30">
        <button
          onClick={() => setCollapsed(false)}
          className={`p-3 rounded-2xl bg-white shadow-lg border border-gray-100 text-gray-600 transition-opacity ${!collapsed ? 'opacity-0 pointer-events-none' : 'opacity-100'}`}
        >
          <Menu size={24} />
        </button>
      </div>

      {/* 2. MOBILE BACKDROP (Darkens screen when menu is open) */}
      {!collapsed && (
        <div 
          className="md:hidden fixed inset-0 bg-black/20 backdrop-blur-sm z-20"
          onClick={() => setCollapsed(true)}
        />
      )}

      {/* 3. SIDEBAR */}
      <div className={`
        bg-white h-screen fixed top-0 left-0 border-r border-gray-100 transition-all duration-500 ease-in-out z-40 flex flex-col
        ${collapsed ? '-translate-x-full md:translate-x-0 md:w-20' : 'translate-x-0 w-64'}
      `}>
        
        {/* Logo / Toggle Area */}
        <div className="p-6 mb-4 flex items-center justify-between">
          {(!collapsed) && <span className="font-black text-xl tracking-tighter text-gray-800">bane</span>}
          <button
            className="p-2 rounded-xl bg-gray-50 text-gray-500 hover:bg-gray-100 transition-colors"
            onClick={() => setCollapsed(!collapsed)}
          >
            {collapsed ? <Menu size={20} /> : <X size={20} />}
          </button>
        </div>

        {/* Nav Links */}
        <nav className="flex-1 px-3">
          <ul className="space-y-2">
            {navItems.map((item) => {
              const isActive = location.pathname === item.path;
              const Icon = item.icon;
              
              return (
                <li key={item.path}>
                  <Link
                    to={item.path}
                    onClick={() => window.innerWidth < 768 && setCollapsed(true)} // Auto-close on mobile after clicking
                    className={`flex items-center gap-4 p-3 rounded-2xl transition-all duration-300 group ${item.color} ${
                      isActive ? 'bg-gray-50 text-gray-900 font-bold' : 'text-gray-500 font-medium'
                    }`}
                  >
                    <Icon size={22} className={`${isActive ? 'text-current' : 'text-gray-400 group-hover:text-current'} transition-colors flex-shrink-0`} />
                    <span className={`whitespace-nowrap overflow-hidden transition-opacity duration-300 ${collapsed ? 'md:opacity-0' : 'opacity-100'}`}>
                      {item.label}
                    </span>
                  </Link>
                </li>
              );
            })}
          </ul>
        </nav>

        {/* Auth Section */}
        <div className="p-4 border-t border-gray-50">
          {!user ? (
            <button onClick={login} className="flex items-center gap-4 w-full p-3 rounded-2xl text-blue-600 hover:bg-blue-50 transition-all font-bold">
              <LogIn size={22} className="flex-shrink-0" />
              <span className={`${collapsed ? 'md:hidden' : 'block'}`}>Log In</span>
            </button>
          ) : (
            <button onClick={logout} className="flex items-center gap-4 w-full p-3 rounded-2xl text-red-500 hover:bg-red-50 transition-all font-bold">
              <LogOut size={22} className="flex-shrink-0" />
              <span className={`${collapsed ? 'md:hidden' : 'block'}`}>Log Out</span>
            </button>
          )}
        </div>
      </div>
    </>
  );
};

export default NavBar;